import { __decorate } from "tslib";
import { attr, observable, volatile } from '@microsoft/fast-element';
import { FoundationElement, TabsOrientation } from '@microsoft/fast-foundation';
import { uniqueId } from '@microsoft/fast-web-utilities';
import { foundationStepperStyles as styles } from './stepper.styles';
import { foundationStepperTemplate as template } from './stepper.template';
/**
 * @tagname %%prefix%%-stepper
 */
export class Stepper extends FoundationElement {
    constructor() {
        super(...arguments);
        this.orientation = TabsOrientation.vertical;
        this.progressValue = 0;
        this.activeTabIndex = 0;
        this.completedIds = new Map();
        this.setTabs = () => {
            this.activeTabIndex = this.getActiveIndex();
            this.tabs.forEach((tab, index) => {
                if (tab.slot === 'tab') {
                    const isActiveTab = this.activeTabIndex === index;
                    const isLast = index + 1 === this.tabIds.length;
                    const tabId = this.tabIds[index];
                    const tabpanelId = this.tabpanelIds[index];
                    tab.setAttribute('id', tabId);
                    tab.setAttribute('aria-selected', isActiveTab ? 'true' : 'false');
                    tab.setAttribute('aria-disabled', isActiveTab ? 'false' : 'true');
                    tab.setAttribute('selected', isActiveTab ? 'true' : 'false');
                    tab.setAttribute('orientation', this.orientation);
                    tab.setAttribute('aria-controls', tabpanelId);
                    tab.setAttribute('aria-controls', tabpanelId);
                    tab.setAttribute('tab-position', String(index + 1));
                    tab.setAttribute('isLast', String(isLast));
                    tab.setAttribute('tabindex', isActiveTab ? '0' : '-1');
                    if (isActiveTab) {
                        this.activetab = tab;
                        this.activeid = tabId;
                    }
                }
            });
        };
        this.getCompletedTabs = () => {
            this.tabs.forEach((tab, index) => {
                const id = this.tabIds[index];
                const isCompleted = tab.isCompleted();
                this.completedIds.set(id, isCompleted);
            });
            let completedCount = 0;
            for (const value of this.completedIds.values()) {
                if (value === true) {
                    completedCount = completedCount + 1;
                }
            }
            this.progressValue = Math.round((completedCount / this.tabpanels.length) * 100);
        };
        this.setTabPanels = () => {
            this.tabpanels.forEach((tabpanel, index) => {
                const tabId = this.tabIds[index];
                const tabpanelId = this.tabpanelIds[index];
                tabpanel.setAttribute('id', tabpanelId);
                tabpanel.setAttribute('aria-labelledby', tabId);
                this.activeTabIndex !== index
                    ? tabpanel.setAttribute('hidden', '')
                    : tabpanel.removeAttribute('hidden');
            });
        };
    }
    orientationChanged() {
        if (this.$fastController.isConnected) {
            this.setTabs();
            this.setTabPanels();
        }
    }
    activeidChanged(oldValue, newValue) {
        if (this.$fastController.isConnected && this.tabs.length <= this.tabpanels.length) {
            this.setTabs();
            this.setTabPanels();
            this.getCompletedTabs();
        }
    }
    tabsChanged() {
        if (this.$fastController.isConnected && this.tabs.length <= this.tabpanels.length) {
            this.tabIds = this.getTabIds();
            this.tabpanelIds = this.getTabPanelIds();
            this.setTabs();
            this.setTabPanels();
        }
    }
    tabpanelsChanged() {
        if (this.$fastController.isConnected && this.tabpanels.length <= this.tabs.length) {
            this.tabIds = this.getTabIds();
            this.tabpanelIds = this.getTabPanelIds();
            this.setTabs();
            this.setTabPanels();
        }
    }
    connectedCallback() {
        super.connectedCallback();
        this.tabIds = this.getTabIds();
        this.tabpanelIds = this.getTabPanelIds();
        this.activeTabIndex = this.getActiveIndex();
    }
    getActiveIndex() {
        const id = this.activeid;
        if (id !== undefined) {
            return this.tabIds.indexOf(this.activeid) === -1 ? 0 : this.tabIds.indexOf(this.activeid);
        }
        else {
            return 0;
        }
    }
    get disabledPreviousStep() {
        return this.activeTabIndex === 0 ? true : false;
    }
    get isLastStep() {
        return this.activeTabIndex + 1 === this.tabs.length ? true : false;
    }
    get islastStepValid() {
        var _a;
        const lastStepIndex = this.tabs.length - 1;
        const isLastStep = this.activeTabIndex === lastStepIndex;
        if (isLastStep &&
            ((this.validation && ((_a = this.validation[lastStepIndex]) === null || _a === void 0 ? void 0 : _a.isValid())) ||
                !this.validation ||
                !this.validation[lastStepIndex])) {
            this.tabs[lastStepIndex].completed = true;
            this.getCompletedTabs();
            return false;
        }
        else {
            this.tabs[lastStepIndex].completed = false;
            this.getCompletedTabs();
            return true;
        }
    }
    _submit() {
        this.$emit('submit');
    }
    nextStep() {
        this.$emit('next-step');
        if (this.validation && this.validation[this.activeTabIndex]) {
            if (!this.validation[this.activeTabIndex].isValid()) {
                this.tabs[this.activeTabIndex].completed = false;
                this.tabs[this.activeTabIndex].error = true;
                this.getCompletedTabs();
                return;
            }
            this.tabs[this.activeTabIndex].error = false;
        }
        if (this.activeTabIndex + 2 <= this.tabIds.length) {
            this.tabs[this.activeTabIndex].completed = true;
            this.activeTabIndex = this.activeTabIndex + 1;
            this.activeid = this.tabIds[this.activeTabIndex];
            this.$emit('validation-success');
        }
    }
    previousStep() {
        if (this.activeTabIndex !== 0) {
            this.tabs[this.activeTabIndex].completed = false;
            this.activeTabIndex = this.activeTabIndex - 1;
            this.activeid = this.tabIds[this.activeTabIndex];
        }
    }
    getTabIds() {
        return this.tabs.map((tab) => {
            var _a;
            return (_a = tab.getAttribute('id')) !== null && _a !== void 0 ? _a : `tab-${uniqueId()}`;
        });
    }
    getTabPanelIds() {
        return this.tabpanels.map((tabPanel) => {
            var _a;
            return (_a = tabPanel.getAttribute('id')) !== null && _a !== void 0 ? _a : `panel-${uniqueId()}`;
        });
    }
}
__decorate([
    attr
], Stepper.prototype, "orientation", void 0);
__decorate([
    attr
], Stepper.prototype, "activeid", void 0);
__decorate([
    observable
], Stepper.prototype, "tabs", void 0);
__decorate([
    observable
], Stepper.prototype, "tabpanels", void 0);
__decorate([
    observable
], Stepper.prototype, "validation", void 0);
__decorate([
    observable
], Stepper.prototype, "progressValue", void 0);
__decorate([
    observable
], Stepper.prototype, "activeTabIndex", void 0);
__decorate([
    volatile
], Stepper.prototype, "disabledPreviousStep", null);
__decorate([
    volatile
], Stepper.prototype, "isLastStep", null);
__decorate([
    volatile
], Stepper.prototype, "islastStepValid", null);
// explicitly declaring for extensibility
export const foundationStepperShadowOptions = undefined;
export const defaultStepperConfig = {};
/**
 * The Foundation Stepper
 *
 * @public
 * @remarks
 * HTML Element: \<foundation-stepper\>
 */
export const foundationStepper = Stepper.compose(Object.assign({ baseName: 'stepper', template,
    styles, shadowOptions: foundationStepperShadowOptions }, defaultStepperConfig));
